import React, { useRef } from 'react';
import { Avatar, Box, Button, Stack, Typography } from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { toast } from '@/lib/toast';

const MAX_BYTES = 2 * 1024 * 1024;

const PhotoUploadField = ({ value, onChange, label = 'Photo', size = 80 }) => {
  const inputRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('Image is too large (max 2MB)');
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      onChange(reader.result);
    };
    reader.onerror = () => {
      toast.error('Failed to read image');
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleRemove = () => {
    onChange(null);
  };

  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 1 }}>
        <Avatar
          src={value || undefined}
          sx={{
            width: size,
            height: size,
            bgcolor: 'background.default',
            color: 'text.secondary',
            border: 1,
            borderColor: 'divider',
          }}
        />
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PhotoCameraIcon />}
            onClick={() => inputRef.current?.click()}
          >
            {value ? 'Change' : 'Upload'}
          </Button>
          {value && (
            <Button
              variant="text"
              size="small"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={handleRemove}
            >
              Remove
            </Button>
          )}
        </Stack>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handleFile}
          data-testid="photo-upload-input"
        />
      </Stack>
    </Box>
  );
};

export default PhotoUploadField;
